import React, { FC, useCallback, useMemo } from 'react';

import { DateTime, Info, StringUnitLength } from 'luxon';

import { BaseStyleEnums, ReactTable, ShortCodeEnums, TimeZoneEnums } from '@nhl/nhl-breakaway-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';

import { ScheduleViewClassName } from '../../../../enums';
import { ScheduleGame } from '../../../../types';

import Day from './components/day/Day';

import { CalendarContainer, CalendarTable, WeekDayContainer } from './styles';

type Props = {
    date?: DateTime;
    games?: Array<ScheduleGame>;
    team: ShortCodeEnums.TeamTriCode;
    timezone: TimeZoneEnums;
};

//TODO: Add in unit tests
const generateMonthBase = (date: DateTime): Array<string> => {
    const monthBase: Array<string> = [];
    //add leading nulls for blank days
    if (date.weekday !== 7) {
        for (let i = 0; i < date.weekday; i++) {
            monthBase.push('');
        }
    }

    if (date.daysInMonth !== undefined) {
        //adding in our date keys
        for (let i = 0; i < date.daysInMonth; i++) {
            monthBase.push(date.plus({ days: i }).toISODate() || '');
        }

        //add ending nulls for blank days
        if (date.plus({ days: date.daysInMonth - 1 }).weekday === 7) {
            for (let i = 0; i < 6; i++) {
                monthBase.push('');
            }
        } else {
            for (let i = 0; i < 6 - date.plus({ days: date.daysInMonth - 1 }).weekday; i++) {
                monthBase.push('');
            }
        }
    }

    return monthBase;
};

const generateWeekDays = (dateOption: StringUnitLength): Array<string> => {
    const weekDays: Array<string> = [
        // Add leading nulls for blank days
        Info.weekdays(dateOption)[6],
    ];
    for (let i = 0; i < 6; i++) {
        weekDays.push(Info.weekdays(dateOption)[i]);
    }
    return weekDays;
};

type WeekColumn = { [dayOfWeek: number]: string } | undefined;

const columnHelper = createColumnHelper<WeekColumn>();

const Calendar: FC<Props> = ({ date, games, team, timezone }) => {
    const size = useSizeWithThrottle();
    const isMedium = size.width >= BaseStyleEnums.ScreenWidthBreakpoints.medium;
    const weekDays = useMemo(() => generateWeekDays(isMedium ? 'long' : 'short'), [isMedium]);

    const gameList = useMemo<{ [gameDate: string]: Array<ScheduleGame> }>(() => {
        const gl = {};
        games?.forEach((game) => (gl[game.gameDate] ? gl[game.gameDate].push(game) : (gl[game.gameDate] = [game])));

        return gl;
    }, [games]);

    const monthBase = useMemo(() => {
        if (date !== undefined) {
            return generateMonthBase(date);
        }
        return undefined;
    }, [date]);

    const massagedData: Array<WeekColumn> = [];

    monthBase?.forEach((day, index) => {
        if (index % 7 === 0) {
            const week = {
                0: day,
            };
            massagedData.push(week);
        } else {
            const week = massagedData.pop();
            if (week) {
                week[index % 7] = day;
            }
            massagedData.push(week);
        }
    });

    const createCalendarColumn = useCallback(
        (day: number, index: number): ColumnDef<WeekColumn> =>
            columnHelper.display({
                id: `day${day}`,
                header: () => <WeekDayContainer key={index}>{weekDays[day]}</WeekDayContainer>,
                size: 40,
                enableSorting: false,
                cell: (data) => {
                    let datum: string | null = null;
                    if (data.row.original !== undefined) {
                        datum = data.row.original[index];
                    }

                    return (
                        <Day
                            key={index}
                            day={datum}
                            games={datum ? gameList[datum] : []}
                            team={team}
                            timezone={timezone}
                        />
                    );
                },
            }),
        [gameList, team, timezone, weekDays]
    );

    // memoizing this seems to prevent rerenders in the table? not sure why
    const columns: Array<ColumnDef<WeekColumn>> = useMemo(
        () => [0, 1, 2, 3, 4, 5, 6].map((day, index) => createCalendarColumn(day, index)),
        [createCalendarColumn]
    );

    return (
        <CalendarContainer id={ScheduleViewClassName.CALENDAR}>
            <CalendarTable>
                <ReactTable data={massagedData} columns={columns} />
            </CalendarTable>
        </CalendarContainer>
    );
};

export default Calendar;
